import { FormDrawer, FormDrawerContent, isEmail } from "@dgs/core";
import { TFunction } from "i18next";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { DataFieldIndexResource, DataFieldType } from "../../../../shared/types/dataField";
import { DataFieldEditField } from "../../contactUpdateInquiries/sections/DataFieldEditField";
import { ContactUpdateFormState } from "../../shared/types/contact";
import { DataFieldValueResource } from "../../shared/types/dataField";

const PHONE_REGEX = /^[+]*[-\s./0-9]{0,4}[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;
const getDataFieldError = (value: any, type: DataFieldType, t: TFunction): string | null => {
	if (type === DataFieldType.EMAIL && value?.length > 0 && !isEmail(value)) {
		return t("Invalid email address");
	}
	if (type === DataFieldType.PHONE && value?.length > 0) {
		if (value.length > 20) {
			return t("This field may have only max. 20 characters");
		} else if (!PHONE_REGEX.test(value)) {
			return t("Invalid phone number");
		}
	}
	if (type === DataFieldType.TEXT && value?.length > 0 && value.length > 181) {
		return t("This field may have only max. 181 characters");
	}
	if (type === DataFieldType.URL && value?.length > 0 && !/^https?:\/\//.test(value)) {
		return t("Invalid URL format");
	}
	if (type === DataFieldType.NUMBER && value != null && !/^\d+$/.test(value)) {
		return t("This field must be an integer");
	}
	return null;
};

export const ContactUpdateDrawer: FC<{
	open: boolean;
	onClose: () => void;
	dataFields: DataFieldIndexResource[];
	values: DataFieldValueResource[];
	onSubmit: (values: ContactUpdateFormState) => void;
}> = ({ open, onClose, dataFields, values, onSubmit }) => {
	const { t } = useTranslation();

	const validate = useCallback(
		(values: ContactUpdateFormState) => {
			const errors: { [K in keyof ContactUpdateFormState]?: any } = {};

			dataFields.forEach((dataField) => {
				if (!dataField) {
					throw new Error("Unknown data field");
				}

				const dataFieldError = getDataFieldError(values[dataField.id], dataField.type, t);

				if (dataFieldError) {
					errors[dataField.id] = dataFieldError;
				}
			});

			return errors;
		},
		[t, dataFields],
	);

	const getDataFieldValue = useCallback(
		(dataField: DataFieldIndexResource) => {
			const dataFieldValue = values.find((value) => value.id === dataField.id);

			switch (dataField.type) {
				case "checkboxGroup":
				case "multiSelectField":
					return !dataFieldValue?.value ? [] : dataFieldValue.value;
				case "radioField":
					return !dataFieldValue?.value ? null : `${dataFieldValue.value}`;
			}

			return dataFieldValue?.value;
		},
		[values],
	);

	const createInitialValues = useCallback(() => {
		return dataFields.reduce(
			(prev, curr) => ({ ...prev, [curr.id]: getDataFieldValue(curr) }),
			{} as ContactUpdateFormState,
		);
	}, [dataFields, getDataFieldValue]);

	return (
		<FormDrawer
			title={t("Edit contact")}
			onClosed={() => void 0}
			open={open}
			onClose={onClose}
			initialValues={createInitialValues()}
			validate={validate}
			onSubmit={async (values) => {
				onSubmit(values);
				return true;
			}}
			renderForm={(props) => (
				<FormDrawerContent {...props}>
					{dataFields.map((dataField) => (
						<DataFieldEditField key={dataField.id} dataField={dataField} />
					))}
				</FormDrawerContent>
			)}
		/>
	);
};
